<template>
  <v-container class="pa-0 no-margins">
    <div class="title-icon top">
      <h2>{{ reportDetail.subElement.element.name }}</h2>
      <v-icon small class="mt-n2 mr-n2" @click.prevent="$emit('close')">$sl-close</v-icon>
    </div>
    <h5>{{ reportDetail.subElement.name }}</h5>
    <template v-if="reportDetail.subElement.description">
      <p>{{ reportDetail.subElement.description }}</p>
    </template>
    <v-toolbar
      v-if="isEditable"
      class="bottomnav mt-2 mb-4"
      flat
      dense
      @click="$emit('changeContentClick', 'addElement')"
    >
      <v-btn :aria-label="$t('ebook.elementpart-change')" icon>
        <v-icon size="24">$sl-arrow-left</v-icon>
      </v-btn>
      <v-toolbar-title class="subtitle-2 semibold">{{ $t('ebook.elementpart-change') }}</v-toolbar-title>
    </v-toolbar>
    <v-divider class="my-4 fullwidth"></v-divider>

    <!-- Show student achievement level and required level to pass the course -->
    <template v-if="judgementId > 0 && judgementId < reportDetail.subElement.requiredLevel">
      <h6> {{ $t('judgementDetail.achievement-level', { level: judgementId }) }} </h6>
      <p> {{ $t('judgementDetail.required-achievement-level', { level: get(reportDetail, 'subElement.requiredLevel', '') }) }} </p>
    </template>

    <h6>{{ $t('ebook.judge-text') }}</h6>

    <template v-if="reportDetail">
      <KnowledgeBoxes
        :isApprentice="true"
        :radiobuttons="isEditable"
        :elem="reportDetail"
        @knowledgeClick="knowledgeClick"
      ></KnowledgeBoxes>

      <template>
        <v-row class="mt-3 pt-2 pb-2">
          <v-col class="pt-0 pb-0">
            <p class="mb-0 text-caption">{{ $t('ebookReportElementsModal.explanation-title') }}</p>
          </v-col>
        </v-row>
  
        <v-row class="mb-6">
          <v-col class="pt-0 pb-0 d-flex align-center flex-wrap">
            <v-icon left>$sl-completed</v-icon>
            <p class="mb-0 text-body-2 text-caption">{{ $t('ebookReportElementsModal.icon-explanation-text-3') }}</p>
          </v-col>
        </v-row>
      </template>

      <MessageBox class="mt-2 mb-8" type="info" :closable="true">
        <h6 class="pb-4 title text-uppercase">{{ $t('ebook.qualification-message-title') }}</h6>
        <p>{{ $t('ebook.qualification-message-text') }}</p>
        <p>{{ $t('ebook.qualification-message-text2') }}</p>
        <p> {{ $t('judgementDetail.explanation-text') }} </p>
        <p>
          <a :href="links.education.assessment" target="_blank">{{ $t('ebook.qualification-message-link') }}</a>
        </p>
      </MessageBox>

      <v-textarea
        auto-grow
        outlined
        v-model="judgeComment"
        :label="$t('ebook.judge-comment')"
        type="text"
        rows="3"
        :disabled="!isEditable || reportDetail.subElement.isApproved"
        row-height="30"
      ></v-textarea>
    </template>

    <v-divider class="mb-2 fullwidth"></v-divider>
    <div v-if="isEditable">
      <ActionButton
        class="mt-6"
        @click="saveClick"
        :disabled="judgementId < 0"
        actionIcon="$sl-save"
        v-if="showDeleteBtn"
        >{{ $t('ebook.save-elementpart') }}</ActionButton
      >
      <ActionButton v-else class="mt-6" @click="saveClick" :isDisabled="judgementId < 0 || isImitatingUser()" actionIcon="$sl-save">{{
        $t('ebook.save-elementpart')
      }}</ActionButton>
      <ActionButton
        v-if="showDeleteBtn"
        actionIcon="$sl-delete"
        class="mt-5"
        @click="showModal = true"
        :isDisabled="isImitatingUser()"
        type="application"
        >{{ $t('labels.delete-element') }}</ActionButton
      >

      <ActionButton class="mb-6 mt-5" type="application" actionIcon="$sl-cancel" @click="$emit('close')">{{
        $t('labels.cancel')
      }}</ActionButton>
    </div>
    <ModalBase
      :show="showModal"
      maxWidth="400px"
      :title="$t('ebook.delete-element-text')"
      cancelIcon="$sl-cancel"
      submitIcon="$sl-delete"
      :cancelText="$t('labels.cancel')"
      :submitText="$t('labels.delete')"
      @close="closeModal"
      @cancel="closeModal"
      @submit="deleteClick"
      :outlinedCancel="false"
      :outlinedSubmit="true"
    >
      <template v-slot:text>
        {{ $t('ebook.delete-element-text2') }}
      </template>
    </ModalBase>
  </v-container>
</template>

<script>
import {mapActions, mapGetters} from 'vuex'
import API from '@/services/api.js'
import MessageBox from '@/components/Common/MessageBox'
import ActionButton from '@/components/Common/ActionButton'
import KnowledgeBoxes from '@/components/Common/Ebook/KnowledgeBoxes'
import ModalBase from '@/components/Common/ModalBase'
import ModalBaseMixin from '@/components/mixins/ModalBaseMixin'
import get from 'lodash/get'

// @group Common
// Styled EbookentModalJudgement component
export default {
  name: 'EbookentModalJudgement',

  mixins: [ModalBaseMixin],

  props: {
    //has elements
    elements: Array,
    //report has current details
    repDetail: Object,
    //has current report
    currentReport: Object,
    isInstructor: {
      // `true` / `false`
      type: Boolean,
      // The default value is: `false`
      default: false
    },
    // Should fields be editable
    isEditable: {
      // `true` / `false`
      type: Boolean,
      // The default value is: `true`
      default: true
    }
  },

  components: {
    ActionButton,
    MessageBox,
    KnowledgeBoxes,
    ModalBase
  },

  data: () => ({
    links: require('../../../lib/external-links.json'),
    judgeComment: '',
    search: '',
    judgementId: -1,
    showDeleteBtn: false,
    reportDetail: {
      subElement: {
        name: '',
        description: '',
        element: {
          name: ''
        }
      }
    }
  }),

  created() {
    this.init()
  },

  watch: {
    repDetail: {
      handler: function () {
        this.init()
      },
      deep: true
    }
  },

  computed: {
    ...mapGetters('currentuser', ['currentUser', 'isApprentice', 'isImitatingUser'])
  },

  filters: {},
  methods: {
    get,

    init() {
      this.reportDetail = this.repDetail

      if (this.reportDetail.apprenticeComment) {
        this.judgeComment = this.reportDetail.apprenticeComment
      }
      if (this.reportDetail.apprenticeshipSkillEstimatedLevel) {
        this.judgementId = this.reportDetail.apprenticeshipSkillEstimatedLevel
      }
      if (this.reportDetail.reportId && this.reportDetail.reportId > 0) {
        this.showDeleteBtn = true
      } else {
        this.showDeleteBtn = false
      }
    },

    knowledgeClick(id) {
      this.judgementId = id
    },
    /**
     * @vuese
     * Used to update the report detail if it exist or create a new one
     * @arg The arguments are integers representing book id , report id,report detail id
     */
    saveClick() {
      if (this.judgementId < 0) {
        this.$events.fire('showError', 'Välj nivå')
        return
      }
      let params

      if (this.reportDetail.id && this.reportDetail.id > 0) {
        if (this.isInstructor === true) {
          params = {instructorSkillEstimatedLevel: this.judgementId, instructorComment: this.judgeComment}
        } else {
          params = {
            apprenticeshipSkillEstimatedLevel: this.judgementId,
            apprenticeComment: this.judgeComment,
            subElementId: this.reportDetail.subElement.id
          }
        }

        API.updateReportDetail(this.currentReport.bookId, this.currentReport.id, this.reportDetail.id, {
          data: params
        })
          .then(response => {
            this.$events.fire('showInfo', this.$t('success.saved'))
            // Fire close
            // @arg The argument is a string
            this.$emit('close')
          })
          .catch(error => {
            console.log('Error: ' + error)
            this.$emit('close')
          })
      } else {
        if (this.isInstructor === true) {
          params = {
            instructorSkillEstimatedLevel: this.judgementId,
            instructorComment: this.judgeComment,
            subElementId: this.reportDetail.subElement.id
          }
        } else {
          params = {
            apprenticeshipSkillEstimatedLevel: this.judgementId,
            apprenticeComment: this.judgeComment,
            subElementId: this.reportDetail.subElement.id
          }
        }

        API.createReportDetail(this.currentReport.bookId, this.currentReport.id, {
          data: params
        })
          .then(response => {
            //this.report = response.data
            this.$events.fire('showInfo', this.$t('success.saved'))
            // Fire close
            // @arg The argument is a string
            this.$emit('close')
          })
          .catch(error => {
            console.log('Error: ' + error)
          })
      }
    },
    /**
     * @vuese
     * Used to delete report
     * @arg the argument is an integer representing report detail id
     */
    deleteClick() {
      this.showModal = false
      if (this.currentReport.id && this.reportDetail.id) {
        API.deleteReportDetail(this.currentReport.bookId, this.currentReport.id, this.reportDetail.id)
          .then(response => {
            this.$events.fire('showInfo', this.$t('success.deleted'))
            // Fire deleted
            // @arg The argument is a integer representing report detail id
            this.$emit('deleted', this.reportDetail.id)
          })
          .catch(error => {
            this.$events.fire('showError', this.$t('errors.delete-failure'))
            console.log('There was an error:', error)
          })
      }
    }
  }
}
</script>

<style lang="scss">
</style>